import React, { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang, formatEUR } from "@/i18n";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const T = (es, en, va) => ({ es, en, va });

const FIELDS = [
  { name: "full_name", label: T("Nombre completo", "Full name", "Nom complet"), col: 2 },
  { name: "email", label: T("Email", "Email", "Email"), type: "email" },
  { name: "phone", label: T("Teléfono", "Phone", "Telèfon") },
  { name: "address", label: T("Dirección de envío", "Shipping address", "Adreça d'enviament"), col: 2 },
  { name: "postal_code", label: T("Código postal", "Postal code", "Codi postal") },
  { name: "city", label: T("Ciudad", "City", "Ciutat") },
  { name: "province", label: T("Provincia", "Province", "Província"), col: 2 },
];

export const CheckoutForm = ({ open, onOpenChange }) => {
  const { items, subtotal } = useCart();
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", address: "", postal_code: "", city: "", province: "", notes: "" });
  const [giftCode, setGiftCode] = useState("");
  const [gift, setGift] = useState(null); // { code, balance } when applied
  const [giftLoading, setGiftLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyGift = async () => {
    const code = giftCode.trim();
    if (!code) return;
    setGiftLoading(true);
    try {
      const { data } = await axios.post(`${API}/gift-cards/validate`, { code });
      if (data.valid) {
        setGift({ code: data.code, balance: data.balance });
        toast.success(t(T("Tarjeta aplicada", "Gift card applied", "Targeta aplicada")));
      } else {
        setGift(null);
        toast.error(data.message || t(T("Código no válido", "Invalid code", "Codi no vàlid")));
      }
    } catch {
      setGift(null);
      toast.error(t(T("No se pudo validar el código", "Could not validate code", "No s'ha pogut validar el codi")));
    } finally {
      setGiftLoading(false);
    }
  };

  const giftApplied = gift ? Math.min(gift.balance, subtotal) : 0;
  const toPay = Math.max(0, subtotal - giftApplied);

  const digitalOnly = items.length > 0 && items.every((i) => i.product_id === "tarjeta-regalo");
  const visibleFields = FIELDS.filter((f) => !digitalOnly || ["full_name", "email", "phone"].includes(f.name));

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        customer: { ...form, notes: form.notes || null },
        gift_code: gift ? gift.code : null,
        items: items.map((i) => ({
          product_id: i.product_id,
          weight: i.weight || null,
          grind: i.grind || null,
          denomination: i.denomination || null,
          gift: i.gift || null,
          design: i.design || null,
          quantity: i.quantity,
        })),
      };
      const { data } = await axios.post(`${API}/checkout/session`, payload);
      if (data.url) { window.location.href = data.url; } else { throw new Error("no url"); }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const realMsg = typeof detail === "object" ? (detail.message || `${detail.error_type}`) : (typeof detail === "string" ? detail : null);
      console.error("Checkout error:", realMsg || err);
      toast.error(realMsg || t(T("No se pudo iniciar el pago. Inténtalo de nuevo.", "Could not start payment. Please try again.", "No s'ha pogut iniciar el pagament. Torna-ho a intentar.")));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checkout-form-dialog" className="bg-[#FBF9F6] border-[#E3DCD2] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight text-[#1A1514]">{t(T("Datos de envío", "Shipping details", "Dades d'enviament"))}</DialogTitle>
          <DialogDescription className="text-[#5C544F]">{t(T("Completa tus datos para preparar el pedido. Pago seguro con Stripe.", "Fill in your details so we can prepare your order. Secure payment with Stripe.", "Completa les teues dades per a preparar la comanda. Pagament segur amb Stripe."))}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-2 gap-3 mt-2">
          {visibleFields.map((f) => (
            <div key={f.name} className={f.col === 2 ? "col-span-2" : "col-span-2 sm:col-span-1"}>
              <label className="text-xs text-[#5C544F]">{t(f.label)} *</label>
              <Input
                data-testid={`checkout-${f.name}`}
                type={f.type || "text"}
                required
                value={form[f.name]}
                onChange={set(f.name)}
                className="mt-1 bg-white border-[#E3DCD2]"
              />
            </div>
          ))}
          <div className="col-span-2">
            <label className="text-xs text-[#5C544F]">{t(T("Notas del pedido (opcional)", "Order notes (optional)", "Notes de la comanda (opcional)"))}</label>
            <Textarea data-testid="checkout-notes" value={form.notes} onChange={set("notes")} rows={2} className="mt-1 bg-white border-[#E3DCD2]" />
          </div>

          {/* Gift card redemption */}
          <div className="col-span-2 border-t border-[#E3DCD2] pt-4">
            <label className="text-xs text-[#5C544F]">{t(T("¿Tienes una tarjeta regalo?", "Have a gift card?", "Tens una targeta regal?"))}</label>
            <div className="flex gap-2 mt-1">
              <Input
                data-testid="gift-code-input"
                value={giftCode}
                onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                placeholder="RITUAL-XXXX-XXXX-XXXX"
                className="bg-white border-[#E3DCD2] font-mono"
                disabled={!!gift}
              />
              {gift ? (
                <Button type="button" data-testid="gift-remove" variant="outline" onClick={() => { setGift(null); setGiftCode(""); }} className="border-[#E3DCD2] whitespace-nowrap">
                  {t(T("Quitar", "Remove", "Llevar"))}
                </Button>
              ) : (
                <Button type="button" data-testid="gift-apply" onClick={applyGift} disabled={giftLoading} className="bg-[#6B705C] hover:bg-[#565a4a] text-white whitespace-nowrap">
                  {giftLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t(T("Aplicar", "Apply", "Aplicar"))}
                </Button>
              )}
            </div>
            {gift && (
              <p data-testid="gift-balance" className="text-xs text-[#6B705C] mt-2">
                {t(T("Saldo disponible", "Available balance", "Saldo disponible"))}: {formatEUR(gift.balance)} · {t(T("se aplicará", "applying", "s'aplicarà"))} {formatEUR(giftApplied)}
              </p>
            )}
          </div>

          <div className="col-span-2 border-t border-[#E3DCD2] pt-4 mt-1 space-y-1">
            <div className="flex justify-between text-sm text-[#5C544F]">
              <span>{t(T("Subtotal", "Subtotal", "Subtotal"))}</span><span>{formatEUR(subtotal)}</span>
            </div>
            {gift && (
              <div className="flex justify-between text-sm text-[#6B705C]">
                <span>{t(T("Tarjeta regalo", "Gift card", "Targeta regal"))}</span><span>−{formatEUR(giftApplied)}</span>
              </div>
            )}
            <div className="flex justify-between items-baseline pt-1">
              <span className="text-sm text-[#1A1514]">{t(T("Total a pagar", "Total to pay", "Total a pagar"))}</span>
              <span data-testid="checkout-total" className="font-display text-2xl text-[#1A1514]">{formatEUR(toPay)}</span>
            </div>
          </div>
          <Button
            data-testid="checkout-pay-button"
            type="submit"
            disabled={loading}
            className="col-span-2 w-full bg-[#1A1514] hover:bg-[#3B2A22] text-[#FBF9F6] rounded-sm h-12 text-base"
          >
            {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t(T("Procesando…", "Processing…", "Processant…"))}</>)
              : toPay <= 0 ? t(T("Confirmar pedido (pagado con tarjeta)", "Confirm order (paid with gift card)", "Confirmar comanda (pagat amb targeta)"))
              : t(T("Pagar de forma segura", "Pay securely", "Pagar de forma segura"))}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
